import type { FileSaveMessage, FileOpenMessage } from '@vvf/shared/bridge';

/**
 * Salvataggio file unificato browser ↔ app nativa.
 *
 * Nel browser: download classico via anchor + blob URL.
 * Dentro il wrapper nativo (WebView): i download blob NON funzionano, quindi
 * inviamo il file in base64 al nativo (messaggio bridge `file:save`), che lo
 * salva su disco e apre il foglio di condivisione/salvataggio (expo-sharing).
 *
 * #2: prima export Excel/PDF/CSV e download documenti "non facevano nulla"
 * nell'app nativa proprio perche' il WebView ignora i download del browser.
 */

interface ReactNativeWebViewWindow {
  ReactNativeWebView?: { postMessage(message: string): void };
}

/** True se la PWA gira dentro il wrapper React Native (WebView). */
export function isInNativeWebView(): boolean {
  return typeof window !== 'undefined'
    && typeof (window as ReactNativeWebViewWindow).ReactNativeWebView?.postMessage === 'function';
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // result = "data:<mime>;base64,XXXX" -> teniamo solo la parte base64.
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Salva/scarica un Blob. Gestisce automaticamente browser vs app nativa.
 * Async: nel WebView serve leggere il blob in base64 prima di inviarlo.
 */
export async function saveFile(data: Blob, filename: string, mimeType: string): Promise<void> {
  if (isInNativeWebView()) {
    const base64 = await blobToBase64(data);
    const msg: FileSaveMessage = { type: 'file:save', filename, mimeType, base64 };
    (window as unknown as Required<ReactNativeWebViewWindow>).ReactNativeWebView.postMessage(JSON.stringify(msg));
    return;
  }
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Apre/visualizza un Blob. Nel browser apre il blob in una nuova scheda (il
 * browser lo renderizza inline: PDF/immagini). Dentro il wrapper nativo
 * `window.open(blob:…)` non apre nulla, quindi inviamo il file in base64 al
 * nativo (messaggio `file:open`) che lo apre col visualizzatore di sistema.
 */
export async function openFile(data: Blob, filename: string, mimeType: string): Promise<void> {
  if (isInNativeWebView()) {
    const base64 = await blobToBase64(data);
    const msg: FileOpenMessage = { type: 'file:open', filename, mimeType, base64 };
    (window as unknown as Required<ReactNativeWebViewWindow>).ReactNativeWebView.postMessage(JSON.stringify(msg));
    return;
  }
  const url = URL.createObjectURL(data);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
